<template>
  <transition-group
      name="notification"
      tag="div"
      class="notification-container"
      @before-enter="beforeEnter"
      @enter="enter"
      @leave="leave"
  >
    <div
        v-for="(item, index) in notifications"
        :key="item.id"
        :class="['notification', `type-${item.type}`]"
        :style="getNotificationStyle(item.type)"
        @click="removeNotification(index)"
        :data-index="index"
    >
      <div class="tag" :style="getTagStyle(item.type)">
        {{ typeTags[item.type] }}
        <div class="tag-highlight"></div>
      </div>
      <img :src="item.avatar" class="avatar" :style="getAvatarStyle(item.type)">
      <div class="content">
        <span class="username">{{ item.username }}</span>
        <span class="separator">·</span>
        <span class="message">{{ formatMessage(item.message) || typeMessages[item.type] }}</span>
        <span v-if="item.gift" class="gift-name">{{ item.gift.name }}</span>
        <span v-if="item.resource" class="resource-name">{{ item.resource.name }}</span>
        <span v-if="item.card" class="card-info">
          {{ truncateText(item.card.position, 3) }}
          <span v-if="item.card.company" class="card-company">
            ({{ truncateText(item.card.company, 3) }})
          </span>
        </span>
      </div>
      <div v-if="item.type === 'redpacket'" class="redpacket-icon">🧧</div>
      <div v-if="item.type === 'photo'" class="photo-icon">📸</div>
      <div v-if="item.type === 'resource'" class="resource-icon">📦</div>
      <div v-if="item.type === 'card'" class="card-icon">📇</div>
      <div v-if="item.type === 'system'" class="system-icon">⚙️</div>
      <div class="glow-effect"></div>
    </div>
  </transition-group>
</template>

<script>
import gsap from 'gsap'
export default {
  data() {
    return {
      notifications: [],
      typeTags: {
        sign: '签到',
        wish: '祝福',
        gift: '礼物',
        recharge: '充值',
        redpacket: '红包',
        photo: '照片',
        resource: '资源',
        card: '名片',
        system: '系统'
      },
      typeMessages: {
        sign: '前来报道',
        wish: '送上了祝福',
        gift: '送出了礼物',
        recharge: '充值支持',
        redpacket: '送出了祝福红包',
        photo: '分享了照片',
        resource: '分享了资源',
        card: '分享了名片',
        system: '系统消息'
      },
      colorPalettes: {
        sign: {
          primary: '#FF9A8B',
          secondary: '#FF6B95'
        },
        wish: {
          primary: '#8EC5FC',
          secondary: '#4DABF7'
        },
        gift: {
          primary: '#FCCF31',
          secondary: '#F55555'
        },
        recharge: {
          primary: '#A0E7E5',
          secondary: '#6AC5CB'
        },
        redpacket: {
          primary: '#FF6961',
          secondary: '#FF3D3D'
        },
        photo: {
          primary: '#B5EAD7',
          secondary: '#8CD790'
        },
        resource: {
          primary: '#C7CEEA',
          secondary: '#9AB7F3'
        },
        card: {
          primary: '#D8BFD8',
          secondary: '#BA55D3'
        },
        system: {
          primary: '#6C5CE7',
          secondary: '#4834D4'
        }
      }
    }
  },
  methods: {
    formatMessage(message, maxLength = 12) {
      if (!message) return '';
      return message.length > maxLength
          ? message.substring(0, maxLength) + '...'
          : message;
    },
    beforeEnter(el) {
      el.style.opacity = 0
      el.style.transform = 'translateX(-100%) scale(0.5)'
      el.style.filter = 'blur(5px)'
    },
    enter(el, done) {
      gsap.to(el, {
        opacity: 1,
        x: 0,
        scale: 1,
        filter: 'blur(0)',
        duration: 0.8,
        ease: 'elastic.out(1, 0.5)',
        onComplete: () => {
          // 添加持续动画
          gsap.to(el.querySelector('.glow-effect'), {
            opacity: [0.3, 0],
            scale: [1, 1.5],
            duration: 1.5,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut'
          })

          // 图标动画
          if (el.querySelector('.redpacket-icon')) {
            gsap.to(el.querySelector('.redpacket-icon'), {
              rotation: 360,
              duration: 3,
              repeat: -1,
              ease: 'none'
            })
          }

          done()
        }
      })

      // 标签弹跳效果
      gsap.from(el.querySelector('.tag'), {
        y: -20,
        opacity: 0,
        duration: 0.6,
        delay: 0.3,
        ease: 'back.out(1.7)'
      })

      // 头像旋转效果
      gsap.from(el.querySelector('.avatar'), {
        rotation: -180,
        scale: 0,
        duration: 0.8,
        ease: 'back.out(1.7)'
      })
    },
    leave(el, done) {
      gsap.to(el, {
        opacity: 0,
        x: 40,
        scale: 0.7,
        filter: 'blur(5px)',
        duration: 0.6,
        ease: 'back.in(1.7)',
        onComplete: () => {
          if (el.parentNode) {
            el.parentNode.removeChild(el)
          }
          done()
        }
      })
    },
    getNotificationStyle(type) {
      const colors = this.colorPalettes[type]
      return {
        background: `linear-gradient(135deg,
          ${colors.primary} 0%,
          ${this.mixColors(colors.primary, colors.secondary, 30)} 50%,
          ${colors.secondary} 100%)`,
        boxShadow: `0 8px 30px ${this.hexToRGBA(colors.secondary, 0.2)}`,
        border: `2px solid ${this.adjustBrightness(colors.secondary, -10)}`
      }
    },
    getTagStyle(type) {
      const colors = this.colorPalettes[type]
      return {
        background: `linear-gradient(135deg,
          ${this.adjustBrightness(colors.primary, 10)},
          ${this.adjustBrightness(colors.secondary, 10)})`,
        color: this.getContrastColor(colors.secondary),
        boxShadow: `inset 0 2px 4px ${this.hexToRGBA('#ffffff', 0.3)}`
      }
    },
    getAvatarStyle(type) {
      return {
        boxShadow: `0 0 0 2px ${this.colorPalettes[type].secondary}, 0 2px 8px rgba(0,0,0,0.15)`
      }
    },
    hexToRGBA(hex, opacity) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    },
    adjustBrightness(hex, percent) {
      const num = parseInt(hex.replace('#', ''), 16)
      const amt = Math.round(2.55 * percent)
      const R = (num >> 16) + amt
      const G = (num >> 8 & 0x00FF) + amt
      const B = (num & 0x0000FF) + amt
      return `#${(
          0x1000000 +
          (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
          (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
          (B < 255 ? (B < 1 ? 0 : B) : 255)
      ).toString(16).slice(1)}`
    },
    getContrastColor(hex) {
      const r = parseInt(hex.substr(1, 2), 16)
      const g = parseInt(hex.substr(3, 2), 16)
      const b = parseInt(hex.substr(5, 2), 16)
      return (r * 0.299 + g * 0.587 + b * 0.114) > 150 ? '#000' : '#fff'
    },
    mixColors(color1, color2, percent) {
      const hex1 = color1.replace('#', '')
      const hex2 = color2.replace('#', '')

      const r = Math.round(parseInt(hex1.substr(0,2), 16) * (100 - percent)/100 + parseInt(hex2.substr(0,2), 16) * percent/100)
      const g = Math.round(parseInt(hex1.substr(2,2), 16) * (100 - percent)/100 + parseInt(hex2.substr(2,2), 16) * percent/100)
      const b = Math.round(parseInt(hex1.substr(4,2), 16) * (100 - percent)/100 + parseInt(hex2.substr(4,2), 16) * percent/100)

      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },
    addNotification(data) {
      const id = Date.now() + Math.random().toString(36).substr(2, 9)
      this.notifications.push({
        id,
        avatar: data.avatar || this.generateDefaultAvatar(),
        ...data
      })
      setTimeout(() => {
        this.removeNotification(this.notifications.findIndex(n => n.id === id))
      }, 3500)
    },
    removeNotification(index) {
      if (index >= 0) this.notifications.splice(index, 1)
    },
    generateDefaultAvatar() {
      const colors = ['#FF9A8B', '#8EC5FC', '#FCCF31', '#A0E7E5', '#FF6961', '#B5EAD7', '#C7CEEA', '#D8BFD8']
      return `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><circle cx='50' cy='40' r='30' fill='${
          colors[Math.floor(Math.random() * colors.length)]
      }'/><circle cx='50' cy='100' r='50' fill='${
          colors[Math.floor(Math.random() * colors.length)]
      }'/></svg>`
    },
    showSystemMsg(username, avatar, message) {
      this.addNotification({
        type: 'system',
        username,
        avatar,
        message
      })
    },
    showSign(username, avatar, message) {
      this.addNotification({
        type: 'sign',
        username,
        avatar,
        message
      })
    },
    showWish(username, avatar, message) {
      this.addNotification({
        type: 'wish',
        username,
        avatar,
        message
      })
    },
    showGift(username, avatar, giftName) {
      this.addNotification({
        type: 'gift',
        username,
        avatar,
        gift: { name: giftName }
      })
    },
    showRecharge(username, avatar, amount) {
      this.addNotification({
        type: 'recharge',
        username,
        avatar,
        amount
      })
    },
    showRedPacket(username, avatar, message) {
      this.addNotification({
        type: 'redpacket',
        username,
        avatar,
        message: '发来一个祝福红包！'
      })
    },
    showPhoto(username, avatar, photoName) {
      this.addNotification({
        type: 'photo',
        username,
        avatar,
        resource: { name: photoName }
      })
    },
    showResource(username, avatar, resourceName) {
      this.addNotification({
        type: 'resource',
        username,
        avatar,
        resource: { name: resourceName }
      })
    },
    showCard(username, avatar, position, company) {
      this.addNotification({
        type: 'card',
        username,
        avatar,
        card: {
          position,
          company
        },
        message: '分享了名片'
      })
    }
  }
}
</script>

<style lang="less" scoped>
// 变量定义
@font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
@notification-height: 36px;
@notification-padding: 0 15px;
@notification-radius: 18px;
@notification-font-size: 12px;
@notification-max-width: 325px;

// 混合函数
.transition-all(@duration: 0.3s, @easing: cubic-bezier(0.25, 0.8, 0.25, 1)) {
  transition: all @duration @easing;
}

.transform(@value) {
  transform: @value;
}

// 容器样式
.notification-container {
  position: fixed;
  top: 20px;
  left: 10px;
  z-index: 9999;
  pointer-events: none;
  font-family: @font-family;
  perspective: 1000px;
}

// 通知项基础样式
.notification {
  position: relative;
  display: flex;
  align-items: center;
  height: @notification-height;
  padding: @notification-padding;
  margin-bottom: 10px;
  border-radius: @notification-radius;
  font-size: @notification-font-size;
  max-width: @notification-max-width;
  transform-style: preserve-3d;
  will-change: transform, opacity;
  cursor: pointer;
  pointer-events: auto;
  .transition-all();

  &:hover {
    .transform(translateX(0) scale(1.03) translateZ(20px));
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  }
}

// 标签样式
.tag {
  font-size: 10px;
  padding: 3px 10px;
  border-radius: 10px;
  margin-right: 12px;
  position: relative;
  z-index: 1;
  transform-style: preserve-3d;

  .tag-highlight {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent);
    z-index: -1;
  }
}

// 头像样式
.avatar {
  width: 28px;
  height: 28px;
  margin-right: 12px;
  border-radius: 50%;
  transform-style: preserve-3d;
  will-change: transform;
}

// 内容区域
.content {
  min-width: 100px;
  transform-style: preserve-3d;
  //border: 1px solid #333;
  display: flex;
  flex-direction: row;
  align-items: center;
  .username {
    margin-right: 5px;
    max-width: 80px;
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    //border: 1px solid #F34;
  }

  .separator {
    margin: 0 6px;
  }

  .gift-name,
  .resource-name {
    margin-left: 8px;
    font-weight: bold;
  }

  .card-info {
    max-width: 90px;
    margin-left: 8px;

    .card-company {
      font-size: 0.9em;
      opacity: 0.8;
    }
  }
}

// 图标样式
.redpacket-icon,
.photo-icon,
.resource-icon,
.card-icon,
.system-icon {
  font-size: 14px;
  margin-left: 8px;
  display: inline-block;
  will-change: transform;
}

// 发光效果
.glow-effect {
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border-radius: 23px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0) 70%);
  z-index: -1;
  opacity: 0;
  pointer-events: none;
}

// 动画定义
.notification-enter-active {
  transition: all 0.8s cubic-bezier(0.68, -0.6, 0.32, 1.6);
}

.notification-leave-active {
  transition: all 0.6s cubic-bezier(0.68, -0.6, 0.32, 1.6);
}

.notification-enter,
.notification-leave-to {
  opacity: 0;
  .transform(translateX(-100%) rotateY(90deg) scale(0.5));
}

// 类型特定动画
.type-gift {
  .gift-name {
    display: inline-block;
    animation: pulse 1.5s infinite;
  }
}

.type-redpacket {
  .redpacket-icon {
    animation: float 2s infinite ease-in-out;
  }
}

.type-photo {
  .photo-icon {
    animation: flash 1.5s infinite;
  }
}

.type-system {
  .system-icon {
    animation: spin 3s infinite linear;
  }
}

// 阴影效果
.notification::after {
  content: '';
  position: absolute;
  top: 10px;
  left: 10%;
  width: 80%;
  height: 20px;
  background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 80%);
  filter: blur(5px);
  z-index: -1;
  opacity: 0;
  .transition-all();

  .notification:hover & {
    opacity: 1;
  }
}

// 关键帧动画
@keyframes spin {
  0% { .transform(rotate(0deg)); }
  100% { .transform(rotate(360deg)); }
}

@keyframes float {
  0%, 100% { .transform(translateY(0) rotate(-5deg)); }
  50% { .transform(translateY(-5px) rotate(5deg)); }
}

@keyframes pulse {
  0% { .transform(scale(1)); }
  50% { .transform(scale(1.2)); }
  100% { .transform(scale(1)); }
}

@keyframes flash {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

// 响应式设计
@media (max-width: 480px) {
  .notification {
    max-width: calc(100vw - 20px);
    font-size: 11px;
    height: 32px;
    padding: 0 10px;
  }

  .avatar {
    width: 24px;
    height: 24px;
  }

  .tag {
    font-size: 9px;
    padding: 2px 6px;
  }
}
</style>